import React, {
  type ChangeEvent, forwardRef,
  useCallback,
  useState,
} from "react";
import { RapInput, RapButton } from "@dynata/rex-raptor";
import "../../../../scss/manage-users.scss";
import { multiAccountRoles, organizationLevelRoles, singleAccountRoles } from "../manageUsers.page";
import type {
  OrganizationData,
  GetUserRoleAssignmentsResponse,
  UserData,
} from "../../../../services/management-backend/client";
import { useCreateUser } from "../hooks";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AxiosError } from "axios";
import type Account from "models/account";
import { ToastConfig } from "core/widgets/toastConfig.widget";
import { ICreateUserAccount } from "models/user/createUserAccount";
import { Organization } from "models/organization";
import { CreateUser } from "models/user/createUser";

// Constants to store reused strings in the code.
const INACTIVE = "Inactive";
const ADMIN = "Admin";
const EMAIL_ALREADY_IN_USE = "This email address is already in use";
const UNEXPECTED_ERROR = "An unexpected error occurred";
const SUCCESS_MESSAGE = "An email invite has been sent to";

export type InvitePopupProps = {
  onClose: () => void;
  organizationAccountsData: Account[];
  organizationData: OrganizationData;
};
export type createdUserType = {
  accounts: GetUserRoleAssignmentsResponse,
  user_data: UserData,
}
const UserInvitePopup = forwardRef(({
  onClose,
  organizationAccountsData,
  organizationData,
}: InvitePopupProps, ref: React.ForwardedRef<HTMLDialogElement>) => {
  const [formValues, setFormValues] = useState<Array<ICreateUserAccount>>([]);

  // Store the Form State
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    selectedOrganizationRole: '',
    isAccountRolesLocked: false,
    isOrganizationAdmin: false,
    formSubmitted: false,
    emailError: null as string | null,
  });

  // Determine if organization is multi-account or single-account.
  const isMultiAccount = organizationAccountsData.length > 1;

  const {
    mutateAsync: mutateCreateUser
  } = useCreateUser();

  const handleSelectChange = useCallback(
    (number: string, event: ChangeEvent<HTMLSelectElement>) => {
      setFormValues((prevState) => {
        const role = event.target.value;
        if (role === INACTIVE) {
          return prevState.filter((value) => value.number !== number);
        }
        else if (role === Organization.ORGANIZATION_OWNER) {
          // Organization owners get set to the ADMIN role AND is_organization_admin = true.
          const updateState = prevState.filter((value) => value.number !== number);
          setFormState((prevState) =>
            ({ ...prevState, isOrganizationAdmin: true }));
          return [...updateState, { number, role }]
        }
        else {
          const updateState = prevState.filter((value) => value.number !== number);
          return [...updateState, { number, role }];
        }
      });
    },
    [organizationAccountsData.length]
  );

  // Sets all the account data roles to Admin.
  const setAllAccountsToAdmin = useCallback(() => {
    setFormValues(organizationAccountsData.map(({ number }) => ({ number, role: ADMIN })));
  }, [organizationAccountsData]);

  const handleOrganizationSelectChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const role = event.target.value;
    if (role === Organization.ORGANIZATION_OWNER) {
      setFormState((prevState) =>
      ({
        ...prevState,
        selectedOrganizationRole: role,
        isOrganizationAdmin: true,
        isAccountRolesLocked: true
      }));
      setAllAccountsToAdmin();
    } else {
      setFormState((prevState) =>
      ({
        ...prevState,
        selectedOrganizationRole: role,
        isOrganizationAdmin: false,
        isAccountRolesLocked: false
      }));
    }
  }, [organizationAccountsData, setAllAccountsToAdmin])

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      // Sets form submitted to true for field validation purposes (displays warnings if fields not filled out).
      setFormState((prevState) =>
        ({ ...prevState, formSubmitted: true }));

      const { firstName, lastName, email, selectedOrganizationRole } = formState;

      // Validate Form
      if (
        !(
          firstName.trim() !== '' &&
          lastName.trim() !== '' &&
          email.trim() !== '' &&
          (!isMultiAccount || selectedOrganizationRole !== '')
          && formValues.some(({ role }) => role !== INACTIVE)
        )
      ) {
        return;
      }

      // Transform Organization Owner role into Admin for request
      const updatedFormValues = formValues.map(({ number, role }) =>
        ({
          number,
          role: role === Organization.ORGANIZATION_OWNER ? ADMIN : role
        })
      );

      try {
        const formData = new FormData(event.currentTarget);
        const createUser = CreateUser.create(formData, updatedFormValues);
        const requestModel = createUser.toRequestModel();
        
        const { user_data }: any = await mutateCreateUser(requestModel);

        if (user_data) {
          toast.success(
            `${SUCCESS_MESSAGE} ${user_data.username}`,
            ToastConfig
          );
        }

        onClose();
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response && error.response.status === 409) {
            setFormState((prevState) =>
              ({ ...prevState, emailError: EMAIL_ALREADY_IN_USE }));
            return
          }
        }
        setFormState((prevState) =>
          ({ ...prevState, emailError: UNEXPECTED_ERROR }));
      }
    },
    [formValues, formState, mutateCreateUser, onClose]
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const { firstName, lastName, email, selectedOrganizationRole, isAccountRolesLocked, formSubmitted, emailError } = formState;

  return (
    <dialog ref={ref} className="model-popup-container">
      <div className="user_invite_modal">
        <h2 className="label-title">Invite User</h2>
        <form className="edit-popup-form" onSubmit={handleSubmit}>
          <div className="invite_user_input_group">
            <label className="invite_user_input_label"> Name: </label>
            <div className="iu_group">
              <div>
                {/* First Name input */}
                <RapInput
                  inputType="default"
                  type="text"
                  inputSize="large"
                  placeholder="First Name"
                  name="firstName"
                  onChange={handleInputChange}
                />

                {/* Show error label for First Name missing */}
                {formSubmitted && firstName.trim() === '' && <label className="error-label">* First Name is required</label>}
              </div>

              <div>
                {/* Last Name input */}
                <RapInput
                  inputType="default"
                  type="text"
                  inputSize="large"
                  placeholder="Last Name"
                  name="lastName"
                  onChange={handleInputChange}
                />

                {/* Show error label for Last Name missing */}
                {formSubmitted && lastName.trim() === '' && <label className="error-label">* Last Name is required</label>}
              </div>
            </div>
          </div>

          <div className="invite_user_input_group">
            <label className="invite_user_input_label"> Email: </label>
            <div className="iu_group">
              <div>
                {/* Email input */}
                <RapInput
                  inputType="default"
                  type="email"
                  size="large"
                  placeholder="Email"
                  name="email"
                  onChange={handleInputChange}
                />

                {/* Show error label for Email missing */}
                {formSubmitted && email.trim() === '' && <label className="error-label">* Email is required</label>}
                {emailError && <label className="error-label">{emailError}</label>}
              </div>
            </div>
          </div>
          <div className="invite_user_input_group">
            <label className="label-title"> Organization: </label>

            {/* IF Organization is multi-account -> Then display organization level role selection */}
            {isMultiAccount && (
              <>
                <div className={"flex input-container"}>
                  <label className="dark_mode_wrapper" htmlFor="organization-select">{organizationData?.display_name}</label>
                  <select
                    className="dark_mode_wrapper"
                    id="organization-select"
                    name="organization-select"
                    onChange={(event) => {
                      handleOrganizationSelectChange(event)
                    }}
                  >
                    <option value="" disabled selected>Select</option>
                    {organizationLevelRoles.map((role, index) => (
                      <option key={index} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                {/* Show error label for Organization Role missing for multi-account */}
                {formSubmitted && !(!isMultiAccount || selectedOrganizationRole !== '') && <label className="error-label">* Organization Role selection is required</label>}

                <span className="label-title">Accounts</span>

                {/* Show error label for Account Role missing for multi-account */}
                {formSubmitted && !formValues.some(({ role }) => role !== INACTIVE) && <label className="error-label">* At least one Account Role selection is required</label>}
              </>
            )}
            {organizationAccountsData.map(({ name, number }, index) => (
              <div key={index} className={"flex input-container"}>
                <label className="dark_mode_wrapper" htmlFor={`dropdown-${number}`}>{name}</label>
                <select className="dark_mode_wrapper"
                  id={`dropdown-${number}`}
                  name={`dropdown-${number}`}
                  value={formValues.find(value => value.number === number)?.role || (isMultiAccount ? INACTIVE : "")}
                  onChange={(event) => {
                    handleSelectChange(number, event);
                  }}
                  disabled={isAccountRolesLocked}
                >
                  <option value="" disabled selected>Select</option>
                  {isMultiAccount ? multiAccountRoles.map((value, index) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  )) : singleAccountRoles.map((value, index) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            {/* Show error label for Account Role missing for single-account */}
            {!isMultiAccount && formSubmitted && !formValues.some(({ role }) => role !== INACTIVE) && <label className="error-label">* Account Role selection is required</label>}
          </div>
          <div className="invite_user_btn_wrapper">
            <RapButton
              styleType="secondary"
              color="alternative"
              type="button"
              onClick={onClose}
            >
              Cancel
            </RapButton>
            <RapButton type="submit">Invite</RapButton>
          </div>
        </form>
      </div>
    </dialog>
  );
});

export default UserInvitePopup;
